import React, { useState, useCallback } from 'react';
import { Box, CircularProgress, IconButton, Tooltip, Typography, LinearProgress } from '@mui/material';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist/build/pdf';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';
import './App.css';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import SetupPanel from './components/SetupPanel';
import Dashboard from './components/Dashboard';
import HistoryPopover from './components/HistoryPopover';
import { AnalysisMode, CVFile, AnalysisResponse } from './types';
import {
  ShortlistHistoryEntry, loadHistory, addHistoryEntry, removeHistoryEntry,
  clearHistory as clearAllHistory, exportHistory, importHistory,
} from './utils/history';

GlobalWorkerOptions.workerSrc = pdfjsWorker;

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

async function extractTextFromPDF(file: File): Promise<string> {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await getDocument({ data: arrayBuffer }).promise;
  let text = '';
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    text += content.items.map((item: any) => item.str).join(' ') + '\n';
  }
  return text;
}

type AppState = 'setup' | 'analyzing' | 'results';

const AppInner: React.FC = () => {
  const { mode, toggleTheme } = useTheme();
  const isDark = mode === 'dark';

  const [appState, setAppState] = useState<AppState>('setup');
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');
  const [results, setResults] = useState<AnalysisResponse | null>(null);
  const [jobLabel, setJobLabel] = useState('');
  const [error, setError] = useState('');
  const [history, setHistory] = useState<ShortlistHistoryEntry[]>(() => loadHistory());

  const handleAnalyze = useCallback(async (
    mode: AnalysisMode,
    jd: string,
    title: string,
    files: CVFile[],
  ) => {
    setError('');
    setAppState('analyzing');
    setProgress(0);

    const label = mode === 'jd' ? 'Job Description' : mode === 'title' ? title : 'General Analysis';
    setJobLabel(label);

    try {
      // Process in batches of 5
      const BATCH = 5;
      const total = files.length;
      const allResults: any[] = [];

      for (let i = 0; i < total; i += BATCH) {
        const batch = files.slice(i, i + BATCH);
        setProgressLabel(`Analyzing ${i + 1}–${Math.min(i + BATCH, total)} of ${total}…`);

        const res = await fetch(`${API_BASE}/api/shortlist/analyze`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mode, jd, title, files: batch }),
        });

        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.error || `Server error ${res.status}`);
        }

        const data = await res.json();
        allResults.push(...(data.candidates || []));
        setProgress(Math.round(((i + BATCH) / total) * 100));
      }

      // Sort by matchScore descending (nulls last)
      allResults.sort((a, b) => {
        if (a.matchScore === null && b.matchScore === null) return 0;
        if (a.matchScore === null) return 1;
        if (b.matchScore === null) return -1;
        return b.matchScore - a.matchScore;
      });

      // Mark strays (score < 40 in jd/title mode)
      const candidates = allResults.map(c => ({
        ...c,
        isStray: (mode !== 'general') && c.matchScore !== null && c.matchScore < 40,
      }));

      // Compute pool stats
      const scored = candidates.filter(c => c.matchScore !== null);
      const avgMatchScore = scored.length > 0
        ? Math.round(scored.reduce((s: number, c: any) => s + c.matchScore, 0) / scored.length)
        : null;

      const nationalityBreakdown: Record<string, number> = {};
      candidates.forEach((c: any) => {
        if (c.nationality && c.nationality !== '—' && c.nationality !== 'Unknown') {
          nationalityBreakdown[c.nationality] = (nationalityBreakdown[c.nationality] || 0) + 1;
        }
      });

      const topMatch = scored.length > 0 ? scored[0].name : null;

      const finalResults: AnalysisResponse = {
        candidates,
        poolStats: {
          totalCandidates: candidates.length,
          avgMatchScore,
          nationalityBreakdown,
          topMatch,
          straysCount: candidates.filter((c: any) => c.isStray).length,
        },
      };
      setResults(finalResults);
      setAppState('results');

      // Save to history
      const entry: ShortlistHistoryEntry = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        timestamp: Date.now(),
        mode,
        jd,
        title,
        jobLabel: label,
        cvCount: files.length,
        results: finalResults,
      };
      setHistory(prev => addHistoryEntry(prev, entry));
    } catch (e: any) {
      setError(e.message || 'Analysis failed. Please try again.');
      setAppState('setup');
    }
  }, []);

  const handleSelectHistory = useCallback((entry: ShortlistHistoryEntry) => {
    setResults(entry.results);
    setJobLabel(entry.jobLabel);
    setAppState('results');
    setError('');
  }, []);

  const handleDeleteHistory = useCallback((id: string) => {
    setHistory(prev => removeHistoryEntry(prev, id));
  }, []);

  const handleClearHistory = useCallback(() => {
    setHistory(clearAllHistory());
  }, []);

  const handleExportHistory = useCallback(() => {
    exportHistory(history);
  }, [history]);

  const handleImportHistory = useCallback((jsonText: string) => {
    try {
      setHistory(prev => importHistory(jsonText, prev));
    } catch {
      setError('Could not import history — invalid file.');
    }
  }, []);

  return (
    <HelmetProvider>
      <Helmet>
        <title>Shortlist — AI CV Screening</title>
        <meta name="description" content="Upload CVs, set a role, and get AI-ranked candidates with match scores, skills, and gap analysis." />
      </Helmet>

      {/* Ambient background */}
      <div className="ambient-stage">
        <div className="orb orb-one" />
        <div className="orb orb-two" />
        <div className="orb orb-three" />
      </div>

      {/* Top bar */}
      <Box sx={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        px: { xs: 2, sm: 3 }, py: 1.5,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: isDark ? 'rgba(23,18,16,0.82)' : 'rgba(243,232,220,0.82)',
        backdropFilter: 'blur(16px)',
        borderBottom: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 28, height: 28, borderRadius: '8px', background: 'linear-gradient(135deg, #FF8428, #FFB06A)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 800, color: 'white', lineHeight: 1 }}>S</Typography>
          </Box>
          <Typography sx={{ fontWeight: 800, fontSize: '1rem', letterSpacing: '-0.02em', color: isDark ? '#FFF8EF' : '#171615' }}>
            Shortlist
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryPopover
            history={history}
            onSelect={handleSelectHistory}
            onDelete={handleDeleteHistory}
            onClear={handleClearHistory}
            onExport={handleExportHistory}
            onImport={handleImportHistory}
            isDark={isDark}
          />
          <Tooltip title={isDark ? 'Light mode' : 'Dark mode'}>
            <IconButton onClick={toggleTheme} size="small" sx={{ color: 'text.secondary' }}>
              {isDark ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Main content */}
      <Box sx={{ pt: 9, pb: 8, px: { xs: 2, sm: 3 }, minHeight: '100vh' }}>
        <Box sx={{ maxWidth: 900, mx: 'auto' }}>
          {appState === 'setup' && (
            <SetupPanel
              onAnalyze={handleAnalyze}
              extractTextFromPDF={extractTextFromPDF}
              error={error}
            />
          )}

          {appState === 'analyzing' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 3 }}>
              <CircularProgress size={56} thickness={3} sx={{ color: '#FF8428' }} />
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', mb: 0.5 }}>
                  Analyzing CVs…
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                  {progressLabel}
                </Typography>
                <Box sx={{ width: 280, mx: 'auto' }}>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{ height: 6, borderRadius: 3, '& .MuiLinearProgress-bar': { borderRadius: 3, background: 'linear-gradient(90deg, #FF8428, #FFB06A)' } }}
                  />
                </Box>
              </Box>
            </Box>
          )}

          {appState === 'results' && results && (
            <Dashboard
              data={results}
              jobLabel={jobLabel}
              apiBase={API_BASE}
              onBack={() => { setAppState('setup'); setResults(null); setError(''); }}
              isDark={isDark}
            />
          )}
        </Box>
      </Box>
    </HelmetProvider>
  );
};

const App: React.FC = () => (
  <ThemeProvider>
    <AppInner />
  </ThemeProvider>
);

export default App;
